import { useGet } from "@albertine/api-client";
import type {
    CatalogItemSearchResult,
    RecommendationItem,
    RequestRecommendationsResponseData,
    SearchCatalogItemsQuery,
} from "@albertine/types/openapi/lmt";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toCatalogItemsSearchGetParams } from "../../common/CatalogItem";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import SearchResultItem from "./SearchResultItem";
import { WebSearchResultItem } from "./WebSearchResultItem";

type RecommendationResultsProps = {
    className?: string;
    requestId: string;
    defaultSearch: SearchCatalogItemsQuery;
    recommendedCatalogIds: string[];
    selectedRecommendationId: string | undefined;
    onSelect: (recommendationId: string) => void;
    onHover: (id: string | undefined) => void;
    highlightedItemId: string | undefined;
};

export function RecommendationResults(props: RecommendationResultsProps) {
    const {
        className,
        requestId,
        defaultSearch,
        recommendedCatalogIds,
        selectedRecommendationId,
        onSelect,
        onHover,
        highlightedItemId,
    } = props;

    const queryClient = useQueryClient();
    const recommendationsCache =
        queryClient.getQueryData<RequestRecommendationsResponseData>([
            "requestRecommendations",
            { param: { id: requestId } },
        ]);
    const recommendations = recommendationsCache?.items ?? [];

    const catalogIds =
        recommendedCatalogIds.length > 0 ? recommendedCatalogIds : undefined;

    const {
        data: recommendedItemsData,
        isLoading: isLoadingRecommendedItems,
        isPending: isPendingRecommendedItems,
    } = useGet(
        "catalogItemsSearch",
        toCatalogItemsSearchGetParams({
            ids: catalogIds,
            pageSize: catalogIds?.length,
        }),
        {
            enabled: (catalogIds?.length ?? 0) > 0,
            staleTime: 5 * 60 * 1000,
        },
    );

    const recommendedItemsById = new Map(
        (recommendedItemsData?.catalogItems ?? []).map((item) => [item.id, item]),
    );

    const batchLoading =
        (catalogIds?.length ?? 0) > 0 &&
        (isLoadingRecommendedItems || isPendingRecommendedItems);

    const recommendationBatchKey = recommendations.map((item) => item.id).join(",");
    const appliedInitialSelectionBatchRef = useRef<string>("");

    useEffect(() => {
        if (recommendationBatchKey.length === 0) {
            appliedInitialSelectionBatchRef.current = "";
            return;
        }

        if (batchLoading) return;
        if (appliedInitialSelectionBatchRef.current === recommendationBatchKey) return;

        const firstRecommendationId = recommendations[0]?.id;
        if (!firstRecommendationId) return;

        appliedInitialSelectionBatchRef.current = recommendationBatchKey;
        onSelect(firstRecommendationId);
    }, [recommendations, batchLoading, recommendationBatchKey, onSelect]);

    if (recommendations.length === 0) {
        return null;
    }

    if (batchLoading) {
        return (
            <div className="flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="catalog-search-results-container">
            <div className={className}>
                {recommendations.map((recommendation) => (
                    <RecommendationResultRow
                        key={recommendation.id}
                        recommendation={recommendation}
                        catalogItem={recommendedItemsById.get(recommendation.id)}
                        defaultSearch={defaultSearch}
                        selectedRecommendationId={selectedRecommendationId}
                        onSelect={onSelect}
                        onHover={onHover}
                        highlightedItemId={highlightedItemId}
                    />
                ))}
            </div>
        </div>
    );
}

type RecommendationResultRowProps = {
    recommendation: RecommendationItem;
    catalogItem: CatalogItemSearchResult | undefined;
    defaultSearch: SearchCatalogItemsQuery;
    selectedRecommendationId: string | undefined;
    onSelect: (recommendationId: string) => void;
    onHover: (id: string | undefined) => void;
    highlightedItemId: string | undefined;
};

function RecommendationResultRow(props: RecommendationResultRowProps) {
    const {
        recommendation,
        catalogItem,
        defaultSearch,
        selectedRecommendationId,
        onSelect,
        onHover,
        highlightedItemId,
    } = props;

    if (recommendation.source === "web") {
        return (
            <WebSearchResultItem
                item={recommendation}
                selectedItemId={selectedRecommendationId}
                onClick={onSelect}
                onHover={onHover}
                highlightedItemId={highlightedItemId}
            />
        );
    }

    if (!catalogItem) {
        return (
            <div className="catalog-search-results__row-error">
                Could not load catalog item
            </div>
        );
    }

    return (
        <SearchResultItem
            item={catalogItem}
            fitScore={recommendation.score}
            filters={defaultSearch}
            selectedItemId={selectedRecommendationId}
            onClick={() => {
                onSelect(recommendation.id);
            }}
            onHover={onHover}
            highlightedItemId={highlightedItemId}
        />
    );
}
